import './SystemAlertHistory.css'
import SystemAlertDeleteModal from './components/SystemAlertDeleteModal.jsx';
import { useMessageList, useUpdateMultipleMessageReadStatus } from '../../hooks/queries/messageQueries';
import { useAuthContext } from '../../contexts/AuthContext';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AlertItem from './components/AlertItem.jsx';
import RefreshIcon from '../../assets/icons/refresh.svg?react';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import ReadIcon from '../../assets/icons/read.svg?react';
import Pagination from '../../components/Pagination/Pagination';
import { SkeletonTable } from '../../components/Skeleton';
import { SelectAllCheckbox } from '../../components/SelectAllCheckbox';

const SystemAlertHistory = () => {
  const { user } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // 읽음 처리 mutation
  const updateMultipleReadStatus = useUpdateMultipleMessageReadStatus(user?.UserSysId);

  // URL 파라미터에서 상태 가져오기
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const sortBy = searchParams.get('sortBy') || 'IssuedDate';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const title = searchParams.get('Title') || '';
  const type = searchParams.get('Type') || '';
  const isRead = searchParams.get('IsRead') || '';

  // 쿼리 파라미터 메모이제이션
  const queryParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder,
    };
    
    if (title) params.Title = title;
    if (type) params.Type = type;
    if (isRead) params.IsRead = isRead;
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, title, type, isRead]);

  const { 
    data: messages, 
    isLoading, 
    error,
    refetch 
  } = useMessageList(user?.UserSysId, queryParams);

  // 페이지 변경 시 선택 초기화
  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage]);

  // 현재 페이지의 메시지 ID 목록
  const currentPageMessageIds = useMemo(() => {
    if (!messages?.Items) return new Set();
    return new Set(messages.Items.map(msg => msg.DRiveNotificationSysId));
  }, [messages?.Items]);

  // 체크박스 상태 계산 (현재 페이지의 항목만 고려)
  const checkboxState = useMemo(() => {
    // 현재 페이지에서 선택된 항목들만 필터링
    const selectedInCurrentPage = Array.from(selectedIds).filter(id => 
      currentPageMessageIds.has(id)
    );
    const selectedCount = selectedInCurrentPage.length;
    const totalCount = currentPageMessageIds.size;
    
    if (selectedCount === 0) return 'none'; // 빈 상태
    if (selectedCount === totalCount) return 'all'; // 전체 선택
    return 'partial'; // 부분 선택 (1개 이상, 전체-1 이하)
  }, [selectedIds, currentPageMessageIds]);

  // 체크박스 클릭 핸들러
  const handleHeaderCheckboxClick = useCallback(() => {
    if (checkboxState === 'all' || checkboxState === 'partial') {
      // 전체 선택 또는 부분 선택 상태 -> 모두 해제
      setSelectedIds(new Set());
    } else {
      // 빈 상태 -> 전체 선택
      setSelectedIds(new Set(currentPageMessageIds));
    }
  }, [checkboxState, currentPageMessageIds]);

  // 개별 체크박스 클릭 핸들러 (함수형 업데이트로 최신 상태 보장)
  const handleItemCheckboxChange = useCallback((messageId, checked) => {
    setSelectedIds((prevSelectedIds) => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (checked) {
        newSelectedIds.add(messageId);
      } else {
        newSelectedIds.delete(messageId);
      }
      return newSelectedIds;
    });
  }, []);

  // URL 파라미터 업데이트
  const updateSearchParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const handlePageChange = (page) => {
    updateSearchParams({ page: page.toString() });
  };

  // 읽음 처리
  const handleMarkAsRead = async () => {
    if (selectedIds.size === 0 || !user?.UserSysId) return;
    
    try {
      const messageIds = Array.from(selectedIds);
      await updateMultipleReadStatus.mutateAsync(messageIds);
      
      // 선택 초기화
      setSelectedIds(new Set());
    } catch (error) {
      console.error('읽음 처리 실패:', error);
      alert('읽음 처리에 실패했습니다.');
    }
  };

  // Refresh
  const handleRefresh = () => {
    refetch();
    setSelectedIds(new Set());
  };

  const totalPages = messages?.Total 
    ? Math.ceil(messages.Total / pageSize) 
    : 1;

  const handleDeleteSuccess = () => {
    refetch();
    setSelectedIds(new Set());
    setIsDeleteModalOpen(false);
  };

  const handleDelete = (alert) => {
    setIsDeleteModalOpen(true);
  };

  return (
    <div className="system-alert-history-container page-container">
      <div className="system-alert-history-content">
        <div className='flex align-center gap-5 pl-8'>
          {/* 상단 체크박스 */}
          <SelectAllCheckbox
            state={checkboxState}
            onClick={handleHeaderCheckboxClick}
            size={20}
          />
          
          {/* Refresh 버튼 */}
          <button 
            className='btn icon-btn' 
            onClick={handleRefresh}
            title="새로고침"
          >
            <RefreshIcon color='var(--gray-600)' />
          </button>
          
          {/* 읽음 처리 버튼 */}
          <button 
            className='btn icon-btn' 
            onClick={handleMarkAsRead}
            disabled={selectedIds.size === 0 || updateMultipleReadStatus.isPending}
            title="읽음 처리"
          >
            <ReadIcon color={selectedIds.size === 0 ? 'var(--gray-400)' : 'var(--gray-600)'} />
          </button>
          <button 
            className='btn icon-btn' 
            onClick={handleDelete}
            disabled={selectedIds.size === 0}
            title="알림 삭제"
          >
            <DeleteIcon color={selectedIds.size === 0 ? 'var(--gray-400)' : 'var(--gray-600)'} />
          </button>
        </div>
        
        {isLoading ? (
          <SkeletonTable rows={10} cols={5} />
        ) : error ? (
          <div className="error-message">데이터를 불러오는 중 오류가 발생했습니다.</div>
        ) : (
          <>
            <div className="primary-table">
              <table>
                <thead>
                  <tr>
                    <th width="50px"></th>
                    <th width="50px"></th>
                    <th width="100px">유형</th>
                    <th>제목</th>
                    <th>발생일시</th>
                  </tr>
                </thead>
                <tbody>
                  {messages?.Items?.length > 0 ? (
                    messages.Items.map(alert => (
                      <AlertItem 
                        key={alert.DRiveNotificationSysId} 
                        alert={alert}
                        isSelected={selectedIds.has(alert.DRiveNotificationSysId)}
                        onCheckboxChange={handleItemCheckboxChange}
                        onDelete={handleDelete}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-data">알림 내역이 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="pagination-wrapper">
              <Pagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={handlePageChange} 
              />
            </div>
          </>
        )}
      </div>
      {
        isDeleteModalOpen && (
        <SystemAlertDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDeleteSuccess={handleDeleteSuccess}
          selectedAlert={Array.from(selectedIds)}
        />
      )}
    </div>
  )
}

export default SystemAlertHistory